class WholesaleInquiryForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.submitButton = null;
    this.successMessage = null;
    this.errorMessage = null;
  }

  connectedCallback() {
    // Wait for DOM to be ready
    setTimeout(() => {
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('.wholesale-inquiry-form__button');
      this.successMessage = this.querySelector('.wholesale-inquiry-form__success');
      this.errorMessage = this.querySelector('.wholesale-inquiry-form__errors');
      
      if (this.form) {
        // Prevent any default form submission
        this.form.addEventListener('submit', this.handleSubmit.bind(this), true);
        // Also prevent form action from triggering
        this.form.setAttribute('onsubmit', 'return false;');
      }
    }, 0);
  }

  handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Disable submit button and show loading state
    this.submitButton.disabled = true;
    this.submitButton.classList.add('loading');
    this.originalButtonText = this.submitButton.textContent;
    this.submitButton.textContent = 'Submitting...';
    
    // Hide any previous messages
    if (this.successMessage) this.successMessage.style.display = 'none';
    if (this.errorMessage) this.errorMessage.style.display = 'none';
    
    // Get form data
    const formData = new FormData(this.form);
    
    // Submit via JSONP
    this.submitViaJsonp(formData)
      .then((response) => {
        if (response.result === 'success') {
          // Send notification email via FormSubmit
          this.sendNotificationEmail(formData).catch((err) => {
            console.warn('Notification email failed:', err);
          });
          
          // Check if we should also create Shopify customer
          const createShopifyCustomer = this.dataset.createShopifyCustomer === 'true';
          
          if (createShopifyCustomer) {
            return this.createShopifyCustomer(formData)
              .then(() => response)
              .catch(() => {
                // Don't fail if Shopify fails, Mailchimp succeeded
                console.warn('Shopify customer creation failed, but Mailchimp succeeded');
                return response;
              });
          }
          return response;
        } else {
          throw new Error(response.msg || 'Submission failed');
        }
      })
      .then((response) => {
        this.showSuccess();
        this.form.reset();
      })
      .catch((error) => {
        console.error('Error:', error);
        this.showError(error.message || 'There was an error submitting your request. Please try again.');
      })
      .finally(() => {
        this.resetButton();
      });
  }

  submitViaJsonp(formData) {
    return new Promise((resolve, reject) => {
      // Create unique callback name
      const callbackName = 'mailchimpCallback_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      
      // Create global callback function
      window[callbackName] = (data) => {
        // Clean up
        delete window[callbackName];
        if (script.parentNode) {
          document.body.removeChild(script);
        }
        
        resolve(data);
      };
      
      // Convert form action to JSON endpoint
      const formAction = this.form.getAttribute('data-mailchimp-action');
      const jsonUrl = formAction.replace('/subscribe/post', '/subscribe/post-json');
      
      // Build URL with parameters
      const params = new URLSearchParams(formData);
      params.append('c', callbackName);
      
      const fullUrl = jsonUrl + (jsonUrl.includes('?') ? '&' : '?') + params.toString();
      
      // Create script tag
      const script = document.createElement('script');
      script.src = fullUrl;
      
      script.onerror = () => {
        delete window[callbackName];
        if (script.parentNode) {
          document.body.removeChild(script);
        }
        reject(new Error('Failed to connect to Mailchimp'));
      };
      
      document.body.appendChild(script);
    });
  }

  resetButton() {
    this.submitButton.disabled = false;
    this.submitButton.classList.remove('loading');
    this.submitButton.textContent = this.originalButtonText;
  }

  async sendNotificationEmail(formData) {
    console.log('sendNotificationEmail called');
    const firstName = formData.get('FNAME') || 'Someone';
    const email = formData.get('EMAIL') || '';
    const message = `${firstName} with email ${email} just filled out the wholesale inquiry form`;
    
    console.log('About to send FormSubmit with data:', { message, firstName, email });
    
    const notificationData = new FormData();
    notificationData.append('message', message);
    notificationData.append('_subject', 'New Wholesale Inquiry');
    notificationData.append('_template', 'box');
    notificationData.append('_cc', 'cchilds@themuzeo.com,dev@ojasllc.net,tdowell@themuzeo.com');
    notificationData.append('_captcha', 'false');
    
    console.log('Making fetch request to FormSubmit...');
    const response = await fetch('https://formsubmit.co/dany@apptimist.studio', {
      method: 'POST',
      body: notificationData,
      headers: {
        'Accept': 'application/json'
      }
    });
    
    console.log('FormSubmit response:', response.status, response.ok);
    
    if (!response.ok) {
      throw new Error('Notification email failed');
    }
  }

  async createShopifyCustomer(formData) {
    // Create a new FormData for Shopify customer form
    const shopifyFormData = new FormData();
    shopifyFormData.append('form_type', 'customer');
    shopifyFormData.append('utf8', '✓');
    shopifyFormData.append('contact[tags]', 'wholesale-inquiry');
    shopifyFormData.append('contact[first_name]', formData.get('FNAME') || '');
    shopifyFormData.append('contact[email]', formData.get('EMAIL') || '');
    shopifyFormData.append('contact[phone]', formData.get('PHONE') || '');
    shopifyFormData.append('contact[note][Company]', formData.get('CNAME') || '');
    
    const response = await fetch('/contact', {
      method: 'POST',
      body: shopifyFormData,
      headers: {
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Shopify customer creation failed');
    }
  }
  
  showSuccess() {
    if (this.successMessage) {
      this.successMessage.style.display = 'block';
      this.successMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    
    // Hide form fields after success
    const formWrapper = this.querySelector('.wholesale-inquiry-form__form-wrapper');
    if (formWrapper) {
      formWrapper.querySelector('.wholesale-inquiry-form__fields')?.remove();
      formWrapper.querySelector('.wholesale-inquiry-form__submit')?.remove();
    }
  }
  
  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.style.display = 'block';
      this.errorMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      alert(message);
    }
  }
}

customElements.define('wholesale-inquiry-form', WholesaleInquiryForm);
